import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastInfo } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { useServerErrorMessage } from '@/components/organisms/FishGroupForms';
import { useUpdateStockingCycle } from '@/domain/stockingCycles';
import { getIsoWeek, getIsoWeekYear, now } from '@/utils/date';

import type { BoardCycle } from '../../types';
import {
    editStockingDefaults,
    type NewStockingFormValues,
    stockedBeforePlannedWeek,
    toUpdateStockingCycleInput,
} from '../NewStockingModal/newStockingForm';
import StockingForm from '../NewStockingModal/StockingForm';

interface EditStockingModalProps {
    clientId: number;
    locationId: number;
    /** Real names: the dialog never mounts in demo mode. */
    cycle: BoardCycle;
    /** Every stocking at the site; its own name doesn't count as taken. */
    cycles: BoardCycle[];
    onClose: () => void;
    onSaved: () => void;
}

/**
 * The New stocking form for a stocking that exists, from its banner: every field it stores
 * (name, planned week, notes), prefilled. Status and stocked weeks stay the groups' own.
 */
export default function EditStockingModal(props: EditStockingModalProps) {
    const { clientId, locationId, cycle, cycles, onClose, onSaved } = props;
    const { t } = useTranslation('fishGroups');
    const otherNames = useMemo(
        () => cycles.filter((other) => other.id !== cycle.id).map((other) => other.name),
        [cycles, cycle.id]
    );
    // Fixed for the dialog's lifetime: a refetch while it is open doesn't reset the form.
    const [{ defaults, followName }] = useState(() =>
        editStockingDefaults(cycle, otherNames, now())
    );
    // Its own year saves as it is, however old; another year must be in New stocking's window.
    const ownYear = Number(defaults.year);
    const { update, error, isLoading, reset } = useUpdateStockingCycle(clientId, locationId);
    const serverError = useServerErrorMessage(error);

    const close = () => {
        if (isLoading) return;
        reset();
        onClose();
    };

    const onSubmit = async (submitted: NewStockingFormValues) => {
        const input = toUpdateStockingCycleInput(submitted, defaults, now(), ownYear);
        if (!input) return;
        const saved = await update(cycle.id, input).then(
            () => true,
            () => false
        );
        if (!saved) return;
        ToastInfo({
            content: t('editStocking.successToast', { name: input.name }),
            status: 'success',
            icon: 'success',
            options: { toastId: 'stocking-cycle-update-success', autoClose: 5000 },
        });
        reset();
        onSaved();
    };

    const stockedFrom = cycle.stockedFrom;
    const body = (
        <StockingForm
            defaults={defaults}
            followName={followName}
            takenNames={otherNames}
            withNotes
            ownYear={ownYear}
            submitLabel={t('editStocking.submit')}
            note={t('editStocking.derivedNote')}
            warning={(values) =>
                stockedFrom && stockedBeforePlannedWeek(stockedFrom, values, now(), ownYear) ? (
                    <Alert tone="warning" size="small">
                        {t('editStocking.stockedBeforePlanned', {
                            week: getIsoWeek(stockedFrom),
                            year: getIsoWeekYear(stockedFrom),
                        })}
                    </Alert>
                ) : null
            }
            serverError={serverError}
            onCancel={close}
            onSubmit={onSubmit}
        />
    );

    return (
        <ModalComponent
            isOpen
            onClose={close}
            title={t('editStocking.title', { name: cycle.name })}
            body={body}
            backdrop="static"
        />
    );
}
